<template>
  <PageLayout :title="$t('admin.integrations_title')" :subtitle="$t('admin.integrations_subtitle')">
    <ContentCard>
      <div v-if="loading" class="text-muted">{{ $t('common.loading') }}</div>
      <div v-else class="table-responsive">
        <table class="table table-hover integrations-table">
          <thead>
            <tr>
              <th>{{ $t('admin.integrations_col_name') }}</th>
              <th>{{ $t('global.status') }}</th>
              <th>{{ $t('admin.integrations_col_env') }}</th>
              <th>{{ $t('admin.integrations_col_storage') }}</th>
              <th class="text-right">{{ $t('global.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="panel in panels" :key="panel.key">
              <td>
                <div class="integrations-table__name">
                  <i :class="[panel.icon, 'integrations-table__icon']" aria-hidden="true"></i>
                  <span>{{ panel.title }}</span>
                </div>
              </td>
              <td>
                <span class="badge" :class="panel.data.configured ? 'badge-success' : 'badge-secondary'">
                  {{ panel.data.configured ? $t('admin.integration_active') : $t('admin.integration_inactive') }}
                </span>
              </td>
              <td>
                <span
                  v-if="panel.data.mode"
                  class="badge"
                  :class="panel.data.mode === 'live' ? 'badge-danger' : 'badge-warning'"
                >
                  {{ paymentEnvLabel(panel.data.mode) }}
                </span>
                <span v-else class="text-muted">—</span>
              </td>
              <td>
                <span v-if="panel.data.stored_in_database" class="badge badge-info">{{ $t('admin.integration_saved') }}</span>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-right">
                <button type="button" class="btn btn-xs btn-outline-primary" @click="openPanel(panel)">
                  {{ panel.editable !== false ? $t('admin.integrations_configure') : $t('common.view') }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </ContentCard>

    <modal name="integration-modal" height="auto" :adaptive="true" width="600" :scrollable="true">
      <div v-if="activePanel" class="modal-pad">
        <div class="integration-modal__head">
          <div class="integration-modal__title">
            <i :class="[activePanel.icon, 'mr-2']" aria-hidden="true"></i>
            {{ activePanel.title }}
          </div>
          <button type="button" class="integration-modal__close" :disabled="!!saving" @click="closePanel">×</button>
        </div>

        <form @submit.prevent="saveActive">
          <div v-for="field in activePanel.fields" :key="field.key">
            <div v-if="field.type === 'section'" class="integration-section-label">{{ field.label }}</div>
            <div v-else class="form-group">
              <label>{{ field.label }}</label>
              <input
                v-if="field.type !== 'select' && field.type !== 'textarea' && !field.readonly"
                v-model="modalForm[field.key]"
                :type="field.type || 'text'"
                class="form-control"
                :placeholder="field.placeholder"
              />
              <textarea
                v-else-if="field.type === 'textarea'"
                v-model="modalForm[field.key]"
                class="form-control"
                rows="6"
                :placeholder="field.placeholder"
              ></textarea>
              <select v-else-if="field.type === 'select'" v-model="modalForm[field.key]" class="form-control">
                <option v-for="opt in field.options" :key="String(opt.value)" :value="opt.value">{{ opt.label }}</option>
              </select>
              <div v-else class="integration-readonly">
                <code>{{ field.value || '-' }}</code>
                <button
                  v-if="field.copy"
                  type="button"
                  class="btn btn-outline-secondary btn-sm"
                  :disabled="!field.value"
                  @click="copyValue(field.value)"
                >
                  {{ $t('common.copy') }}
                </button>
              </div>
              <small v-if="field.hint" class="form-text text-muted">{{ field.hint }}</small>
            </div>
          </div>

          <div v-if="activePanel.key === 'email'" class="mt-3 pt-3 border-top">
            <div class="form-group mb-2">
              <label>{{ $t('admin.email_test_to') }}</label>
              <input v-model="emailTestTo" type="email" class="form-control" :placeholder="$t('global.email')" />
            </div>
            <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="testingEmail" @click="sendTestEmail">
              <i :class="[testingEmail ? 'fas fa-spinner fa-spin' : 'fas fa-paper-plane', 'mr-1']"></i>
              {{ $t('admin.email_send_test') }}
            </button>
          </div>

          <div class="integration-modal__footer">
            <button type="button" class="btn btn-light btn-sm mr-2" :disabled="!!saving" @click="closePanel">
              {{ $t('common.cancel') }}
            </button>
            <button v-if="activePanel.editable !== false" type="submit" class="btn btn-primary btn-sm" :disabled="!!saving">
              <i v-if="saving === activePanel.key" class="fas fa-spinner fa-spin mr-1"></i>
              {{ $t('common.save') }}
            </button>
          </div>
        </form>
      </div>
    </modal>
  </PageLayout>
</template>

<script>
import i18nPage from '@/mixins/i18nPage'
import { getIntegrations, updateIntegration, testEmailIntegration } from '@/api/admin'

export default {
  mixins: [i18nPage],
  data() {
    return {
      loading: true,
      saving: '',
      testingEmail: false,
      emailTestTo: '',
      integrations: null,
      activeKey: '',
      modalForm: {},
      forms: {
        paypal: {
          mode: 'sandbox',
          sandbox_client_id: '',
          sandbox_client_secret: '',
          sandbox_webhook_id: '',
          live_client_id: '',
          live_client_secret: '',
          live_webhook_id: '',
        },
        google: { client_id: '', client_secret: '', redirect_uri: '' },
        instagram: {
          instagram_app_id: '1722813352107738',
          instagram_app_secret: '',
          redirect_uri: '',
          app_review_demo_post_url: '',
          app_review_test_phrase: '',
          app_review_demo_post_id: '',
        },
        facebook: { app_id: '', app_secret: '' },
        pakasir: {
          mode: 'sandbox',
          sandbox_project_slug: '',
          sandbox_api_key: '',
          live_project_slug: '',
          live_api_key: '',
        },
        openai: { base_url: '', api_key: '', model: '' },
        email: {
          host: '',
          port: 587,
          username: '',
          password: '',
          from_email: '',
          from_name: 'SocialLabs',
          use_tls: true,
          welcome_subject: '',
          welcome_body: '',
          invoice_subject: '',
          invoice_body: '',
          renewal_reminder_subject: '',
          renewal_reminder_body: '',
          expired_subject: '',
          expired_body: '',
        },
      },
    }
  },
  computed: {
    panels() {
      const d = this.integrations || {}
      return [
        {
          key: 'paypal',
          title: 'PayPal',
          icon: 'fab fa-paypal',
          data: d.paypal || {},
          fields: this.paypalFields(d),
        },
        {
          key: 'google',
          title: 'Google',
          icon: 'fab fa-google',
          data: d.google || {},
          fields: this.googleFields(d),
        },
        {
          key: 'instagram',
          saveAs: 'meta',
          title: 'Instagram',
          icon: 'fab fa-instagram',
          data: d.instagram || {},
          fields: this.instagramFields(d),
        },
        {
          key: 'facebook',
          saveAs: 'meta',
          title: 'Facebook',
          icon: 'fab fa-facebook',
          data: d.facebook || {},
          fields: this.facebookFields(d),
        },
        {
          key: 'threads',
          title: 'Threads',
          icon: 'fas fa-at',
          editable: false,
          data: d.threads || {},
          fields: this.threadsFields(d),
        },
        {
          key: 'openai',
          title: 'OpenAI',
          icon: 'fas fa-robot',
          data: d.openai || {},
          fields: this.openaiFields(d),
        },
        {
          key: 'pakasir',
          title: this.$t('admin.local_payment_title'),
          icon: 'fas fa-qrcode',
          data: d.pakasir || {},
          fields: this.pakasirFields(d),
        },
        {
          key: 'email',
          title: this.$t('admin.email_title'),
          icon: 'ti-email',
          data: d.email || {},
          fields: this.emailFields(d),
        },
      ]
    },
    activePanel() {
      if (!this.activeKey) return null
      return this.panels.find((p) => p.key === this.activeKey) || null
    },
  },
  async created() {
    await this.load()
  },
  methods: {
    paypalFields(d) {
      return [
        { key: 'mode', label: this.$t('admin.payment_env_active'), type: 'select', options: [
          { value: 'sandbox', label: this.$t('admin.payment_env_sandbox') },
          { value: 'live', label: this.$t('admin.payment_env_live') },
        ], hint: this.$t('admin.payment_env_active_hint') },
        { key: 'sandbox_section', label: this.$t('admin.payment_credentials_sandbox'), type: 'section' },
        { key: 'sandbox_client_id', label: this.$t('admin.paypal_client_id') },
        { key: 'sandbox_client_secret', label: this.$t('admin.secret'), type: 'password', placeholder: d.paypal?.sandbox_client_secret_set ? d.paypal.sandbox_client_secret : '', hint: this.$t('admin.secret_keep_hint') },
        { key: 'sandbox_webhook_id', label: this.$t('admin.paypal_webhook_id') },
        { key: 'live_section', label: this.$t('admin.payment_credentials_live'), type: 'section' },
        { key: 'live_client_id', label: this.$t('admin.paypal_client_id') },
        { key: 'live_client_secret', label: this.$t('admin.secret'), type: 'password', placeholder: d.paypal?.live_client_secret_set ? d.paypal.live_client_secret : '', hint: this.$t('admin.secret_keep_hint') },
        { key: 'live_webhook_id', label: this.$t('admin.paypal_webhook_id') },
        { key: 'webhook_url', label: this.$t('admin.webhook_url'), readonly: true, value: d.paypal?.webhook_url || '' },
      ]
    },
    googleFields(d) {
      return [
        { key: 'client_id', label: this.$t('admin.google_client_id') },
        { key: 'client_secret', label: this.$t('admin.secret'), type: 'password', placeholder: d.google?.client_secret_set ? d.google.client_secret : '', hint: this.$t('admin.secret_keep_hint') },
        { key: 'redirect_uri', label: this.$t('admin.redirect_uri'), hint: this.$t('admin.redirect_uri_hint') },
      ]
    },
    instagramFields(d) {
      return [
        { key: 'instagram_app_id', label: this.$t('admin.instagram_app_id'), hint: this.$t('admin.instagram_app_id_hint') },
        { key: 'instagram_app_secret', label: this.$t('admin.instagram_app_secret'), type: 'password', placeholder: d.instagram?.instagram_app_secret_set ? d.instagram.instagram_app_secret : '', hint: this.$t('admin.instagram_app_secret_hint') },
        { key: 'redirect_uri', label: this.$t('admin.redirect_uri'), readonly: true, copy: true, value: d.instagram?.redirect_uri || '', hint: this.$t('admin.meta_redirect_hint') },
        { key: 'webhook_url', label: this.$t('admin.webhook_url'), readonly: true, copy: true, value: d.instagram?.webhook_url || '' },
        { key: 'webhook_verify_token', label: this.$t('admin.meta_webhook_secret'), readonly: true, copy: true, value: d.instagram?.webhook_verify_token || '', hint: this.$t('admin.instagram_webhook_hint') },
        { key: 'app_review_demo_post_url', label: this.$t('admin.app_review_demo_url'), hint: this.$t('admin.app_review_demo_url_hint') },
        { key: 'app_review_test_phrase', label: this.$t('admin.app_review_test_phrase'), hint: this.$t('admin.app_review_test_phrase_hint') },
        { key: 'app_review_demo_post_id', label: this.$t('admin.app_review_demo_post_id'), type: 'number', hint: this.$t('admin.app_review_demo_post_id_hint') },
      ]
    },
    facebookFields(d) {
      return [
        { key: 'app_id', label: this.$t('admin.meta_app_id'), hint: this.$t('admin.meta_app_id_hint') },
        { key: 'app_secret', label: this.$t('admin.secret'), type: 'password', placeholder: d.facebook?.app_secret_set ? d.facebook.app_secret : '', hint: this.$t('admin.secret_keep_hint') },
        { key: 'redirect_uri', label: this.$t('admin.redirect_uri'), readonly: true, copy: true, value: d.facebook?.redirect_uri || '', hint: this.$t('admin.facebook_redirect_hint') },
        { key: 'webhook_url', label: this.$t('admin.webhook_url'), readonly: true, copy: true, value: d.facebook?.webhook_url || '' },
        { key: 'webhook_verify_token', label: this.$t('admin.meta_webhook_secret'), readonly: true, copy: true, value: d.facebook?.webhook_verify_token || '', hint: this.$t('admin.facebook_webhook_hint') },
      ]
    },
    threadsFields(d) {
      return [
        { key: 'redirect_uri', label: this.$t('admin.redirect_uri'), readonly: true, copy: true, value: d.threads?.redirect_uri || '', hint: this.$t('admin.threads_redirect_hint') },
        { key: 'webhook_url', label: this.$t('admin.webhook_url'), readonly: true, copy: true, value: d.threads?.webhook_url || '' },
        { key: 'webhook_verify_token', label: this.$t('admin.meta_webhook_secret'), readonly: true, copy: true, value: d.threads?.webhook_verify_token || '', hint: this.$t('admin.threads_webhook_hint') },
      ]
    },
    openaiFields(d) {
      return [
        { key: 'base_url', label: this.$t('admin.openai_base_url'), hint: this.$t('admin.openai_base_hint') },
        { key: 'api_key', label: this.$t('admin.api_key'), type: 'password', placeholder: d.openai?.api_key_set ? d.openai.api_key : '', hint: this.$t('admin.secret_keep_hint') },
        { key: 'model', label: this.$t('admin.openai_model'), hint: this.$t('admin.openai_model_hint') },
      ]
    },
    pakasirFields(d) {
      return [
        { key: 'mode', label: this.$t('admin.payment_env_active'), type: 'select', options: [
          { value: 'sandbox', label: this.$t('admin.payment_env_sandbox') },
          { value: 'live', label: this.$t('admin.payment_env_live') },
        ], hint: this.$t('admin.payment_env_active_hint') },
        { key: 'sandbox_section', label: this.$t('admin.payment_credentials_sandbox'), type: 'section' },
        { key: 'sandbox_project_slug', label: this.$t('admin.local_payment_slug') },
        { key: 'sandbox_api_key', label: this.$t('admin.api_key'), type: 'password', placeholder: d.pakasir?.sandbox_api_key_set ? d.pakasir.sandbox_api_key : '', hint: this.$t('admin.secret_keep_hint') },
        { key: 'live_section', label: this.$t('admin.payment_credentials_live'), type: 'section' },
        { key: 'live_project_slug', label: this.$t('admin.local_payment_slug') },
        { key: 'live_api_key', label: this.$t('admin.api_key'), type: 'password', placeholder: d.pakasir?.live_api_key_set ? d.pakasir.live_api_key : '', hint: this.$t('admin.secret_keep_hint') },
        { key: 'webhook_url', label: this.$t('admin.webhook_url'), readonly: true, value: d.pakasir?.webhook_url || '' },
      ]
    },
    emailFields(d) {
      return [
        { key: 'host', label: this.$t('admin.smtp_host') },
        { key: 'port', label: this.$t('admin.smtp_port'), type: 'number' },
        { key: 'username', label: this.$t('admin.smtp_username') },
        { key: 'password', label: this.$t('admin.smtp_password'), type: 'password', placeholder: d.email?.password_set ? d.email.password : '', hint: this.$t('admin.secret_keep_hint') },
        { key: 'from_email', label: this.$t('admin.smtp_from_email') },
        { key: 'from_name', label: this.$t('admin.smtp_from_name') },
        { key: 'use_tls', label: this.$t('admin.smtp_use_tls'), type: 'select', options: [
          { value: true, label: this.$t('common.yes') },
          { value: false, label: this.$t('common.no') },
        ] },
        { key: 'welcome_subject', label: this.$t('admin.email_welcome_subject') },
        { key: 'welcome_body', label: this.$t('admin.email_welcome_body'), type: 'textarea', hint: this.$t('admin.email_template_vars') },
        { key: 'invoice_subject', label: this.$t('admin.email_invoice_subject') },
        { key: 'invoice_body', label: this.$t('admin.email_invoice_body'), type: 'textarea', hint: this.$t('admin.email_template_vars') },
        { key: 'renewal_section', label: this.$t('admin.email_renewal_section'), type: 'section' },
        { key: 'renewal_reminder_subject', label: this.$t('admin.email_renewal_subject') },
        { key: 'renewal_reminder_body', label: this.$t('admin.email_renewal_body'), type: 'textarea', hint: this.$t('admin.email_template_vars_billing') },
        { key: 'expired_section', label: this.$t('admin.email_expired_section'), type: 'section' },
        { key: 'expired_subject', label: this.$t('admin.email_expired_subject') },
        { key: 'expired_body', label: this.$t('admin.email_expired_body'), type: 'textarea', hint: this.$t('admin.email_template_vars_billing') },
      ]
    },
    async load() {
      this.loading = true
      try {
        const { data } = await getIntegrations()
        this.integrations = data.data || data
        this.syncForms()
      } catch (e) {
        this.$toast.error(this.$t('admin.integrations_load_failed'))
      } finally {
        this.loading = false
      }
    },
    syncForms() {
      const d = this.integrations || {}
      this.forms.paypal = {
        mode: d.paypal?.mode || 'sandbox',
        sandbox_client_id: d.paypal?.sandbox_client_id || '',
        sandbox_client_secret: '',
        sandbox_webhook_id: d.paypal?.sandbox_webhook_id || '',
        live_client_id: d.paypal?.live_client_id || '',
        live_client_secret: '',
        live_webhook_id: d.paypal?.live_webhook_id || '',
      }
      this.forms.google = {
        client_id: d.google?.client_id || '',
        client_secret: '',
        redirect_uri: d.google?.redirect_uri || '',
      }
      this.forms.instagram = {
        instagram_app_id: d.instagram?.instagram_app_id || '1722813352107738',
        instagram_app_secret: '',
        redirect_uri: d.instagram?.redirect_uri || '',
        app_review_demo_post_url: d.instagram?.app_review_demo_post_url || '',
        app_review_test_phrase: d.instagram?.app_review_test_phrase || '',
        app_review_demo_post_id: d.instagram?.app_review_demo_post_id || '',
      }
      this.forms.facebook = {
        app_id: d.facebook?.app_id || '',
        app_secret: '',
      }
      this.forms.pakasir = {
        mode: d.pakasir?.mode || 'sandbox',
        sandbox_project_slug: d.pakasir?.sandbox_project_slug || '',
        sandbox_api_key: '',
        live_project_slug: d.pakasir?.live_project_slug || '',
        live_api_key: '',
      }
      this.forms.openai = {
        base_url: d.openai?.base_url || '',
        api_key: '',
        model: d.openai?.model || '',
      }
      this.forms.email = {
        host: d.email?.host || '',
        port: d.email?.port || 587,
        username: d.email?.username || '',
        password: '',
        from_email: d.email?.from_email || '',
        from_name: d.email?.from_name || 'SocialLabs',
        use_tls: d.email?.use_tls !== false,
        welcome_subject: d.email?.welcome_subject || '',
        welcome_body: d.email?.welcome_body || '',
        invoice_subject: d.email?.invoice_subject || '',
        invoice_body: d.email?.invoice_body || '',
        renewal_reminder_subject: d.email?.renewal_reminder_subject || '',
        renewal_reminder_body: d.email?.renewal_reminder_body || '',
        expired_subject: d.email?.expired_subject || '',
        expired_body: d.email?.expired_body || '',
      }
    },
    openPanel(panel) {
      this.activeKey = panel.key
      this.modalForm = { ...this.forms[panel.key] }
      this.$modal.show('integration-modal')
    },
    closePanel() {
      if (this.saving) return
      this.$modal.hide('integration-modal')
      this.activeKey = ''
    },
    async sendTestEmail() {
      if (!this.emailTestTo) {
        this.$toast.error(this.$t('admin.email_test_required'))
        return
      }
      this.testingEmail = true
      try {
        await testEmailIntegration(this.emailTestTo)
        this.$toast.success(this.$t('admin.email_test_sent'))
      } catch (e) {
        this.$toast.error(e.response?.data?.message || this.$t('admin.email_test_failed'))
      } finally {
        this.testingEmail = false
      }
    },
    paymentEnvLabel(mode) {
      return mode === 'live' ? this.$t('admin.payment_env_live') : this.$t('admin.payment_env_sandbox')
    },
    async copyValue(value) {
      if (!value) return
      try {
        await navigator.clipboard.writeText(value)
        this.$toast?.success?.(this.$t('common.copied'))
      } catch (e) {
        this.$toast?.error?.(this.$t('common.copy_failed'))
      }
    },
    saveActive() {
      return this.save(this.activeKey, this.modalForm)
    },
    async save(provider, formData) {
      if (!provider) return
      this.saving = provider
      try {
        const panel = this.panels.find((item) => item.key === provider)
        const payload = { ...(formData || this.forms[provider]) }
        if (provider === 'email') {
          payload.port = Number(payload.port) || 587
        }
        if (provider === 'instagram') {
          payload.app_review_demo_post_id = Number(payload.app_review_demo_post_id) || 0
        }
        const { data } = await updateIntegration(panel?.saveAs || provider, payload)
        this.integrations = data.data || data
        this.syncForms()
        if (this.activeKey === provider) {
          this.modalForm = { ...this.forms[provider] }
        }
        this.$toast.success(this.$t('admin.integrations_saved'))
        this.$modal.hide('integration-modal')
        this.activeKey = ''
      } catch (e) {
        this.$toast.error(this.$t('admin.integrations_save_failed'))
      } finally {
        this.saving = ''
      }
    },
  },
}
</script>

<style scoped>
.modal-pad {
  padding: 1.25rem 1.35rem 1.5rem;
  max-height: 85vh;
  overflow-y: auto;
}

.integrations-table__name {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  font-weight: 500;
}

.integrations-table__icon {
  width: 1.25rem;
  text-align: center;
  color: var(--wc-primary);
  opacity: 0.9;
}

.integration-modal__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.integration-modal__title {
  font-size: 1.1rem;
  font-weight: 600;
}

.integration-modal__close {
  border: none;
  background: transparent;
  font-size: 1.5rem;
  line-height: 1;
  padding: 0;
  color: #6c757d;
  cursor: pointer;
}

.integration-modal__footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid #e8ecf1;
}

.integration-readonly {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.integration-section-label {
  margin-top: 0.75rem;
  margin-bottom: 0.35rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e8ecf1;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6c757d;
}

.integration-readonly code {
  flex: 1;
  min-width: 0;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e8ecf1;
  border-radius: 6px;
  background: #f7f8fa;
  word-break: break-all;
  white-space: normal;
}
</style>
